import { useQueryClient } from "@tanstack/vue-query";
import { Ref } from "vue";

import { useV4bMutation, V4BMutationOpts } from "@/composables/tanstack/use-generic-mutation";
import { useServices } from "@/configuration/plugins/app/app-provider.configuration";
import { SiteQueryKeys } from "@/data/api/site-query-keys";
import { SiteProgrammingRangeQueryKeys } from "@/data/api/v4b/site-programming-range/site-programming-range-query-keys";
import { SiteSectionMutationKeys } from "@/data/api/v4b/site-section/site-section-mutation-keys";
import { SiteSectionId, V4HSiteId } from "@/data/common/ids";
import { PatchSiteSectionRequest } from "@/domains/v4b/site-section/site-section.types";

export function usePatchSectionMutation(
  siteId: Ref<V4HSiteId>,
  sectionId: Ref<SiteSectionId | undefined>,
  opts?: V4BMutationOpts,
) {
  const v4b = useServices().v4b.clientSdk;
  const queryClient = useQueryClient();

  return useV4bMutation({
    mutationKey: sectionId.value ? SiteSectionMutationKeys.patch(sectionId.value) : undefined,
    mutationFn: (command: PatchSiteSectionRequest) => {
      if (!sectionId.value) {
        throw new Error("Section ID is required");
      }

      return v4b.siteSection.patch(sectionId.value, command);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: SiteQueryKeys.sections(siteId),
      });

      await queryClient.removeQueries({
        queryKey: SiteProgrammingRangeQueryKeys.findPlanning(siteId),
      });
    },
    ...opts,
  });
}
